import React, {useState, useCallback, useMemo, useEffect} from 'react';
import PropTypes from 'prop-types';
import {isEmpty} from 'lodash';
import useHandleResize from 'common/hooks/useHandleResize';
import {Box} from '@material-ui/core';
import {palette} from 'app/styles/theme';
import {ExpandableBox} from 'common/componentsV2/ExpandableSections';
import {TypographyBox} from 'common/componentsV2/boxTools';

import ContentLoader from 'common/componentsV2/ContentLoader';
import InnerChip from 'common/componentsV2/ExpressionBuilderV2/InnerChip';

import Tokens from './Tokens';

const TokenMapFilters = ({isLoading, tokenMap, selectedFilters, totalMetrics, metricsCounter, onSelect}) => {
  const style = {
    container: {
      position: 'relative',
    },
    anoMapContainer: {
      backgroundColor: palette.gray['100'],
      overflow: 'hidden',
      borderRadius: 10,
    },
    anoMap: {
      position: 'relative',
      left: 16,
      paddingRight: 32,
      overflowY: 'scroll',
    },
    panelRootStyle: {
      flexShrink: 0,
      padding: 0,
      border: 'none',
      marginTop: 24,
      marginBottom: 16,
    },
    anoMapLabel: {
      display: 'flex',
      alignItems: 'center',
      marginTop: -4,
      marginLeft: -5,
      marginBottom: 16,
    },
    gradBox: {
      background: 'linear-gradient(0deg, rgba(245,247,251,1) 15%, rgba(245,247,251,0) 100%)',
      width: '100%',
      height: '50px',
      position: 'absolute',
      bottom: 0,
    },
  };

  const [isOpen, setIsOpen] = useState('');
  const bodyContainerHeight = useHandleResize([330]);

  const onExpandBoxChange = useCallback(
    (id) => (isExpanded) => {
      setIsOpen((st) => {
        return {...st, [id]: isExpanded};
      });
    },
    [],
  );

  useEffect(() => {
    const first = Object.keys(tokenMap)[0];
    if (first) {
      setIsOpen((st) => {
        return {...st, [first]: true};
      });
    }
  }, [tokenMap]);

  const filteredTokenMap = useMemo(() => Object.keys(tokenMap).slice(0, 30), [tokenMap]);

  return (
    <Box style={style.container}>
      <TypographyBox color={palette.gray['400']} variant="subtitle4" height={16}>
        {!isLoading && !isEmpty(tokenMap) && <span>Dimension Breakdown</span>}
      </TypographyBox>

      <Box style={style.anoMapContainer}>
        {filteredTokenMap.length > 0 && (
          <Box style={style.anoMap} height={bodyContainerHeight}>
            {isLoading && (
              <Box mt={3}>
                <ContentLoader
                  rows={[
                    {width: 90, height: 27, marginBottom: 44},
                    {width: '100%', height: 20, marginBottom: 44},
                    {width: '100%', height: 20, marginBottom: 44},
                    {width: '100%', height: 20},
                  ]}
                />
              </Box>
            )}
            {!isLoading &&
              filteredTokenMap.length &&
              filteredTokenMap.map((what) => (
                <Box key={`anomap-${what}`}>
                  <ExpandableBox
                    automationId="incidentWhatGroup"
                    expanded={isOpen[what]}
                    onChange={onExpandBoxChange(what)}
                    nodeLabel={
                      <Box style={style.anoMapLabel}>
                        <InnerChip title={what} displayOnly type="measure" />
                        {!!selectedFilters.length && !isLoading && (
                          <TypographyBox ml={1} variant="subtitle2" color={palette.teal['500']}>
                            {metricsCounter[what].anomalyOccurrences}/{totalMetrics[what].anomalyOccurrences} Metrics
                          </TypographyBox>
                        )}

                        {!selectedFilters.length && !isLoading && (
                          <TypographyBox ml={1} variant="subtitle2" color={palette.gray['400']}>
                            {metricsCounter[what].anomalyOccurrences} Metrics
                          </TypographyBox>
                        )}
                      </Box>
                    }
                    rootStyle={style.panelRootStyle}
                  >
                    {isOpen[what] && <Tokens tokens={tokenMap[what]} filters={selectedFilters} onSelect={onSelect} />}
                  </ExpandableBox>
                </Box>
              ))}
          </Box>
        )}
      </Box>
      <Box style={style.gradBox} />
    </Box>
  );
};

TokenMapFilters.defaultProps = {
  isLoading: undefined,
};

TokenMapFilters.propTypes = {
  isLoading: PropTypes.bool,
  tokenMap: PropTypes.objectOf(PropTypes.any).isRequired,
  selectedFilters: PropTypes.arrayOf(PropTypes.object).isRequired,
  metricsCounter: PropTypes.objectOf(PropTypes.object).isRequired,
  totalMetrics: PropTypes.objectOf(PropTypes.object).isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default TokenMapFilters;
